import { ArrowUpRight } from 'lucide-react';
import { useLocation } from 'wouter';

import type { LinkCardProps } from '@shared/widget-schemas';
import { cn } from '@/lib/utils';
import { mix } from './chat-widget-ui';
import { capitalizeFirst } from './textFormat';
import { resolveWidgetIcon } from './widget-icons';
import { SkBar, SkCircle } from './skeletons';

const DEFAULT_ACCENT = 'var(--color-purple-400)';

export function LinkCardWidget({
  title,
  description,
  icon,
  label = 'Open',
  navTo,
  accent = DEFAULT_ACCENT,
}: LinkCardProps) {
  const [, setLocation] = useLocation();
  const IconCmp = resolveWidgetIcon(icon, ArrowUpRight);

  return (
    <button
      type="button"
      aria-label={label}
      onClick={() => setLocation(navTo)}
      data-nav-to={navTo}
      className="glass-card flex w-full max-w-[min(560px,100%)] cursor-pointer items-center gap-[13px] p-3.5 text-left"
    >
      <span
        className="inline-flex size-[34px] shrink-0 items-center justify-center rounded-[10px]"
        style={{ background: mix(accent, 14), border: `1px solid ${mix(accent, 28)}`, color: accent }}
      >
        <IconCmp className="size-4" data-icon={icon} aria-hidden />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-[13px] font-bold text-foreground">{capitalizeFirst(title)}</span>
        {description && (
          <span className="text-[11.5px] leading-[1.45] text-muted-foreground">{capitalizeFirst(description)}</span>
        )}
      </div>

      <span
        className="inline-flex shrink-0 items-center gap-1.5 text-[12px] font-semibold"
        style={{ color: accent }}
        aria-hidden
      >
        {label}
        <ArrowUpRight className="size-[14px]" />
      </span>
    </button>
  );
}

export function LinkCardSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'glass-card flex w-full max-w-[min(560px,100%)] items-center gap-[13px] p-3.5',
        className,
      )}
    >
      <SkCircle size="34px" radius="10px" />
      <div className="flex flex-1 flex-col gap-1.5">
        <SkBar width="40%" height="10px" />
        <SkBar width="70%" height="8px" />
      </div>
      <SkBar width="64px" height="10px" />
    </div>
  );
}
